import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getUrlParam, setWebTitle, updateUrlParams } from '@/lib/format-utils';
import DatePickerCustom from '@/components/custom-date-picker';
import { usePayments } from '@/hooks/api-hooks';
import { Payment } from '@turizi/shared';
import { SkeletonList } from '@/components/snippets';
import PaymentsTable from '@/components/payments-table';
import PaymentsSummary from '@/components/payments-summary';
import { Select } from '@mantine/core';
import dayjs from 'dayjs';

interface PaymentsListPageProps {
    direction: 'IN' | 'OUT';
}

export default function PaymentsListPage({ direction }: PaymentsListPageProps): JSX.Element {
    const { t } = useTranslation();
    setWebTitle(direction === 'IN' ? t('payments_income') : t('payments_outcome'));

    const today = dayjs().format('YYYY-MM-DD');

    const [dateRange, setDateRange] = useState<[string, string]>([
        getUrlParam('start', today),
        getUrlParam('end', today)
    ]);

    const [dateType, setDateType] = useState<string>(getUrlParam('date_type', 'created_at'));
    const [clientId, setClientId] = useState<string>(getUrlParam('client_id', ''));
    const [createdById, setCreatedById] = useState<string>(getUrlParam('created_by', ''));


    useEffect(() => {
        if ((dateRange[0] && dateRange[1]) || (!dateRange[0] && !dateRange[1])) {
            updateUrlParams({
                start: dateRange[0] || undefined,
                end: dateRange[1] || undefined,
                date_type: dateType || undefined,
                client_id: clientId || undefined,
                created_by: createdById || undefined,
            });
        }
    }, [dateRange, dateType, clientId, createdById]);

    const {
        data,
        isLoading
    } = usePayments({
        start_day: dateRange[0] || undefined,
        end_day: dateRange[1] || undefined,
        date_type: dateType || undefined,
        direction,
        client_id: clientId || undefined,
        created_by: createdById || undefined,
    }, {
        enabled: (Boolean(dateRange[0]) && Boolean(dateRange[1])) || (!dateRange[0] && !dateRange[1])
    });

    const paymentsList = data?.data || [];

    const clientOptions = useMemo(() => {
        const clientsMap = new Map<string, string>();
        paymentsList.forEach((p: Payment) => {
            const id = direction === 'IN'
                ? (p.from_business_id || p.from_person_id || p.from_business?.id)
                : (p.to_business_id || p.to_person_id || p.to_business?.id);
            const name = direction === 'IN'
                ? (p.from_name || p.from_business?.name || p.from_person?.name)
                : (p.to_name || p.to_business?.name || p.to_person?.name);

            if (id && name) {
                const idStr = String(id);
                if (!clientsMap.has(idStr)) {
                    clientsMap.set(idStr, name);
                }
            }
        });

        return Array.from(clientsMap.entries())
            .map(([value, label]) => ({
                value,
                label
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [paymentsList, direction]);

    const createdByOptions = useMemo(() => {
        const usersMap = new Map<string, string>();
        paymentsList.forEach((p: Payment) => {
            if (p.created_by && p.created_by.id) {
                const idStr = String(p.created_by.id);
                if (!usersMap.has(idStr)) {
                    usersMap.set(idStr, p.created_by.name || p.created_by.email || idStr);
                }
            }
        });

        return Array.from(usersMap.entries())
            .map(([value, label]) => ({
                value,
                label
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [paymentsList]);

    return (
        <div>
            <div className="panel-content">
                <div className="flex items-end gap-2 mb-6 flex-wrap">
                    <Select
                        size="sm"
                        label={t('date_type')}
                        data={[
                            { value: 'payment_date', label: t('payment_date') },
                            { value: 'created_at', label: t('registration_date') }
                        ]}
                        value={dateType}
                        onChange={(val) => setDateType(val || 'created_at')}
                        allowDeselect={false}
                        className="w-40 sm:w-48"
                    />
                    <DatePickerCustom
                        size="sm"
                        valueFormat="DD MMM YYYY"
                        label={dateType === 'created_at' ? t('registration_date') : t('payment_date')}
                        clearable={true}
                        value={dateRange}
                        onChange={(val) => {
                            if ((val[0] && val[1]) || (!val[0] && !val[1])) {
                                setDateRange(val);
                            }
                        }}
                    />
                    <Select
                        size="sm"
                        label={direction === 'IN' ? t('client') : t('provider')}
                        placeholder={t('all')}
                        data={clientOptions}
                        value={clientId || null}
                        onChange={(val) => setClientId(val || '')}
                        clearable
                        searchable
                        className="w-48 sm:w-56"
                    />
                    <Select
                        size="sm"
                        label={t('created_by')}
                        placeholder={t('all')}
                        data={createdByOptions}
                        value={createdById || null}
                        onChange={(val) => setCreatedById(val || '')}
                        clearable
                        searchable
                        className="w-44 sm:w-52"
                    />
                </div>

                {!isLoading && (
                    <PaymentsSummary
                        payments={paymentsList}
                        direction={direction}
                    />
                )}

                <PaymentsTable
                    payments={paymentsList}
                />

                {isLoading && <SkeletonList />}
            </div>
        </div>
    );
}
